
import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { ColorVariant } from '@/lib/colors';

interface ActionButtonProps {
  children: React.ReactNode;
  variant?: ColorVariant | 'cancel';
  icon?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  loading?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  variant = 'default',
  icon,
  className,
  onClick,
  disabled = false,
  type = 'button',
  loading = false
}) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'vibrant':
        return 'bg-primary text-white hover:bg-primary/90 active:bg-primary/80';
      case 'red':
        return 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700';
      case 'green':
        return 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700';
      case 'blue':
        return 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700';
      case 'amber':
        return 'bg-amber-500 text-white hover:bg-amber-600 active:bg-amber-700';
      case 'purple':
        return 'bg-purple-500 text-white hover:bg-purple-600 active:bg-purple-700';
      case 'rose':
        return 'bg-rose-500 text-white hover:bg-rose-600 active:bg-rose-700';
      case 'indigo':
        return 'bg-indigo-500 text-white hover:bg-indigo-600 active:bg-indigo-700';
      case 'cyan':
        return 'bg-cyan-500 text-white hover:bg-cyan-600 active:bg-cyan-700';
      case 'cancel':
        return 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 rounded-md shadow-sm hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100 dark:hover:border-gray-600';
      case 'documentation':
      case 'video':
      case 'link':
        return 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700';
      default:
        return 'bg-accent/10 text-accent hover:bg-accent/20 active:bg-accent/30';
    }
  };

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      className={cn(
        'flex items-center gap-2 px-4 h-10 rounded-lg font-medium text-sm transition-all duration-200 ease-out',
        getVariantClasses(),
        (disabled || loading) && 'opacity-50 cursor-not-allowed hover:bg-opacity-100 hover:shadow-none hover:scale-100',
        className
      )}
      whileHover={!disabled && !loading ? { 
        scale: 1.02, 
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        transition: { duration: 0.2, ease: 'easeOut' }
      } : {}}
      whileTap={!disabled && !loading ? { 
        scale: 0.98, 
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        transition: { duration: 0.1, ease: 'easeOut' }
      } : {}}
    >
      {loading ? (
        <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-1" />
      ) : icon ? (
        <span className="text-current">{icon}</span>
      ) : null}
      {children}
    </motion.button>
  );
};

export default ActionButton;
